import { ROUTES } from '#/config/routes';
import {
  createAmountRangeFilterMeta,
  createDateFilterMeta,
  createFixedMultiFilterMeta,
  createTextFilterMeta,
} from '#/modules/commonComponents/table/columnFilterMeta';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import { createDocumentNavLinksColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import VoucherStatusBadge from '#/modules/finMgmt/vouchers/components/VoucherStatusBadge';
import {
  canEditVoucher,
  formatVoucherStatusLabel,
  formatVoucherTypeLabel,
  VOUCHER_STATUS_FILTER_OPTIONS,
  VOUCHER_TYPE_OPTIONS,
} from '#/modules/finMgmt/vouchers/helper/voucherStatus';
import { formatDateOrDash } from '#/utils/date';
import { formatFmsAmount } from '#/modules/finMgmt/shared/helper/fmsAmount';
import {
  readVoucherDateRangeFilter,
  resolveVoucherDateFilterChipDisplay,
} from '#/modules/finMgmt/shared/helper/fmsListRangeFilters';

function displayDash(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return value;
}

/**
 * 憑證列表欄。
 * 憑證號碼：逗號分隔片段為 OR；總額 `total_amount_min/max`、憑證日期 `voucher_date_from/to` 皆含端點。
 */
export function buildVoucherColumns({
  filters = {},
  onFilterChange,
  onTotalAmountRangeChange,
  onVoucherDateRangeChange,
  mode = 'personal',
  canShowEdit = (row) => canEditVoucher(row.status),
} = {}) {
  const isFinance = mode === 'finance';
  const getDetailPath = (row) => (
    isFinance
      ? ROUTES.fmsVoucherDetail(row.id)
      : ROUTES.personalCenterVoucherDetail(row.id)
  );

  const columns = [
    createDocumentNavLinksColumn({
      getDetailPath,
      getEditPath: (row) => ROUTES.personalCenterVoucherEdit(row.id),
      canShowEdit: isFinance ? () => false : canShowEdit,
    }),
    {
      id: 'voucherNo',
      header: '憑證號碼',
      accessorKey: 'voucherNo',
      accessorFn: (row) => displayDash(row.voucherNo),
      size: 140,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'voucherNo',
          label: '憑證號碼',
          filterValue: filters.voucherNo,
          onChange: (value) => onFilterChange('voucherNo', value),
          placeholder: '搜尋憑證號碼',
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip
          text={displayDash(row.original.voucherNo)}
          detailPath={getDetailPath(row.original)}
          tooltipContent="雙擊開啟憑證"
        />
      ),
    },
    {
      id: 'status',
      header: '狀態',
      accessorKey: 'status',
      accessorFn: (row) => formatVoucherStatusLabel(row.status),
      size: 100,
      enableColumnFilter: true,
      meta: {
        statusKind: 'document',
        ...createFixedMultiFilterMeta({
          key: 'status',
          label: '狀態',
          options: VOUCHER_STATUS_FILTER_OPTIONS,
          filterValue: filters.status,
          onChange: (value) => onFilterChange('status', value),
        }),
      },
      cell: ({ row }) => (
        row.original.status
          ? <VoucherStatusBadge status={row.original.status} size="sm" />
          : '—'
      ),
    },
    {
      id: 'voucherType',
      header: '類型',
      accessorKey: 'voucherType',
      accessorFn: (row) => formatVoucherTypeLabel(row.voucherType),
      size: 100,
      enableColumnFilter: true,
      meta: {
        ...createFixedMultiFilterMeta({
          key: 'voucherType',
          label: '類型',
          options: VOUCHER_TYPE_OPTIONS,
          filterValue: filters.voucherType,
          onChange: (value) => onFilterChange('voucherType', value),
        }),
      },
      cell: ({ row }) => formatVoucherTypeLabel(row.original.voucherType),
    },
    {
      id: 'totalAmount',
      header: '總額',
      accessorKey: 'totalAmount',
      accessorFn: (row) => formatFmsAmount(row.totalAmount),
      size: 120,
      enableColumnFilter: Boolean(onTotalAmountRangeChange),
      meta: {
        cellClassName: 'td_right',
        ...(onTotalAmountRangeChange ? createAmountRangeFilterMeta({
          label: '總額',
          min: filters.totalAmountMin,
          max: filters.totalAmountMax,
          onChange: onTotalAmountRangeChange,
        }) : {}),
      },
    },
    {
      id: 'voucherDate',
      header: '憑證日期',
      accessorKey: 'voucherDate',
      accessorFn: (row) => formatDateOrDash(row.voucherDate),
      size: 148,
      minSize: 148,
      enableColumnFilter: Boolean(onVoucherDateRangeChange),
      ...(onVoucherDateRangeChange ? {
        meta: createDateFilterMeta({
          key: 'voucherDateFrom',
          label: '憑證日期',
          mode: 'range',
          filterValue: readVoucherDateRangeFilter(filters),
          onChange: onVoucherDateRangeChange,
          resolveFilterDisplay: resolveVoucherDateFilterChipDisplay,
        }),
      } : {}),
    },
    ...(isFinance ? [{
      id: 'remark',
      header: '備註',
      accessorFn: (row) => displayDash(row.remark),
      size: 200,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ row }) => <TruncatedTextWithTooltip text={displayDash(row.original.remark)} />,
    }] : []),
  ];

  return columns;
}

export const voucherSideMsgBlocks = [
  {
    fields: [
      { label: '憑證號碼', value: 'voucherNo' },
      { label: '憑證日期', value: 'voucherDate' },
      { label: '總額', value: 'totalAmount' },
      { label: '備註', value: 'remark' },
    ],
  },
];
